import { ref } from 'vue';
import { useLazyQuery } from '@vue/apollo-composable';
import { isNil } from 'lodash-es';
import { usePost } from '~/composables/usePost';
import { API_URL } from '~/utils';
import jobQuery from '~/graphql/job.graphql';
import type { TipoJobEnum } from '~/enums/tipoJobEnum';

export interface JobCommandOptions
{
  path?: string
  interval?: number
  waitForResult?: boolean
}

export function useJobCommand(opts?: JobCommandOptions)
{
  const url = opts?.path
    ? `${API_URL}/${opts.path}`
    : `${API_URL}/job-command`;

  const waitForResult = isNil(opts?.waitForResult)
    ? true
    : opts?.waitForResult;

  const { runAsync: executePost, status: jobCommandStatus, data: jobCommandData } = usePost({
    url,
    config: {
      getAccessToken: true,
      timeout: 120000,
    },
  });

  const options = waitForResult ? { pollInterval: opts?.interval || 2000 } : {};

  const { load, start, stop, onResult, onError } = useLazyQuery(jobQuery, {}, options);

  const poolingStatus = ref<any>(null);
  const poolingProgressData = ref<any>(null);
  const poolingData = ref<any>(null);
  const poolingError = ref<any>(null);

  onResult((queryResult) =>
  {
    if (!queryResult.loading && queryResult.data.model.status === 1)
    {
      poolingProgressData.value = queryResult.data.model;
    }

    if (!queryResult.loading && queryResult.data.model.status === 2)
    {
      poolingProgressData.value = queryResult.data.model;
      stop();
      poolingStatus.value = 'success';
      poolingData.value = queryResult.data.model;
    }

    if (!queryResult.loading && queryResult.data.model.status === 3)
    {
      poolingProgressData.value = queryResult.data.model;
      stop();
      poolingStatus.value = 'error';
      poolingData.value = queryResult.data.model;
      poolingError.value = queryResult.data.model;
    }
  });

  onError(() =>
  {
    poolingStatus.value = 'error';
    poolingData.value = null;
    poolingError.value = null;
  });

  const jobCommand = async (commandType: string, commandData: any, jobType: TipoJobEnum) =>
  {
    poolingStatus.value = 'loading';
    poolingData.value = null;

    await executePost({ commandType, commandData, jobType });

    if (jobCommandStatus.value === 'success')
    {
      if (!waitForResult)
      {
        poolingStatus.value = 'success';
        return;
      }

      await load(null, {
        where: {
          codigo: {
            eq: jobCommandData.value.jobId,
          },
        },
      });

      start();
    }

    if (jobCommandStatus.value === 'error')
    {
      poolingStatus.value = 'error';
    }
  };

  const cancel = () =>
  {
    if (waitForResult)
    {
      stop();
    }
  };

  return {
    cancel,
    data: poolingData,
    progressData: poolingProgressData,
    jobCommand,
    status: poolingStatus,
    err: poolingError,
  };
}
